import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import { Link } from "react-router-dom";
import ScrollReveal from "@/components/base/ScrollReveal";

const painPoints = [
  "想幫孩子準備教育金，但不知道要準備多少",
  "擔心退休後現金流不足",
  "醫療、長照與退休支出沒有整合規劃",
  "資產想傳承，但擔心家庭爭議",
  "想安排保險金、信託或贈與，卻不知道順序",
  "人生階段變動很大，但規劃沒有跟著更新",
];

const coreServices = [
  {
    number: "01",
    title: "教育金規劃",
    desc: "以孩子年齡、教育階段、家庭現金流與預算，規劃教育金路線圖。",
    icon: "ri-graduation-cap-line",
    items: [
      "教育金目標試算",
      "保費預算與彈性調整",
      "基金、ETF 與保單應用",
      "家庭責任與保障同步配置",
    ],
  },
  {
    number: "02",
    title: "退休金現金流",
    desc: "建立多桶金現金流設計，避免退休後只依賴單一收入來源。",
    icon: "ri-calendar-check-line",
    items: [
      "退休年表與缺口測算",
      "固定給付與投資現金流",
      "醫療與長照支出緩衝區",
      "必要支出、彈性支出、願望清單分層",
    ],
  },
  {
    number: "03",
    title: "資產傳承與信託",
    desc: "以保險信託串連資產分配、照護安排與稅務考量，降低爭議與風險。",
    icon: "ri-git-branch-line",
    items: [
      "保險金信託與身故信託",
      "贈與與繼承節點安排",
      "家族治理與共識會議",
      "資產分配與照護安排",
    ],
  },
];

const kpiCards = [
  {
    metric: "100%",
    label: "教育金 / 退休金路線圖可視化",
    icon: "ri-map-line",
  },
  {
    metric: "↑ 穩定",
    label: "退休現金流覆蓋率提升",
    icon: "ri-line-chart-line",
  },
  {
    metric: "0 爭議",
    label: "傳承機制與信託安排清晰",
    icon: "ri-hand-heart-line",
  },
];

const steps = [
  {
    number: "01",
    title: "初談盤點",
    desc: "家庭結構、收入支出、資產負債。",
  },
  {
    number: "02",
    title: "建立地圖",
    desc: "教育金與退休路線圖、缺口測算。",
  },
  {
    number: "03",
    title: "方案設計",
    desc: "現金流、投資、保障的整合。",
  },
  {
    number: "04",
    title: "定期回顧",
    desc: "隨人生階段與家庭狀況調整。",
  },
];

export default function Life() {
  return (
    <div className="min-h-screen bg-brand-black">
      <SEOHead
        title="人生門神 | 教育金・退休規劃・財富傳承 | 保險門神"
        description="人生門神涵蓋教育金規劃、退休金現金流、醫療與長照緩衝、保險金信託、贈與繼承與家族共識。以人生階段為主軸，現金流與風險同步規劃。"
        keywords="教育金規劃,退休規劃,財富傳承,保險信託,長照規劃,保險門神"
        canonicalPath="/services/life"
        ogType="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "人生門神 - 教育金・退休規劃・財富傳承",
          "description": "以人生階段為核心：教育金路線圖、退休現金流設計、資產傳承與保險信託，規劃你與家人的長期安全感。",
          "provider": {
            "@type": "FinancialService",
            "name": "保險門神 Insurance Guardian",
            "url": "__SITE_URL__/"
          },
          "url": "__SITE_URL__/services/life",
          "areaServed": { "@type": "Country", "name": "Taiwan" },
          "serviceType": "教育金、退休規劃與財富傳承"
        }}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20family%20legacy%20background%20with%20golden%20light%20pathways%20on%20deep%20black%20canvas%2C%20subtle%20tree%20of%20life%20motif%20made%20of%20flowing%20gold%20particles%20representing%20generations%20and%20inheritance%2C%20premium%20wealth%20planning%20brand%20aesthetic%2C%20sophisticated%20minimalist%20composition%2C%20warm%20golden%20accents%2C%204K%20quality&width=1920&height=600&seq=104&orientation=landscape"
              alt="人生門神 Hero 背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
          </div>
          <div className="container-main relative z-10">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-[0.9375rem] mb-6">
              <Link to="/" className="text-brand-text-secondary hover:text-brand-gold transition-colors">
                首頁
              </Link>
              <i className="ri-arrow-right-s-line text-brand-text-secondary" />
              <Link to="/services" className="text-brand-text-secondary hover:text-brand-gold transition-colors">
                服務項目
              </Link>
              <i className="ri-arrow-right-s-line text-brand-text-secondary" />
              <span className="text-brand-gold">人生門神</span>
            </div>

            <h1 className="text-white font-display font-bold text-[2rem] md:text-[2.75rem] leading-[1.3] md:leading-[1.25] mb-4">
              人生門神
            </h1>
            <p className="text-brand-gold text-[1.1875rem] font-medium mb-4">
              教育金・退休金・傳承設計
            </p>
            <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] max-w-[800px]">
              以人生階段為核心：教育金、退休現金流、資產傳承與保險信託，規劃你與家人的長期安全感。
            </p>
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  人生常見規劃痛點
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {painPoints.map((point, index) => (
                <ScrollReveal key={index} delay={index * 80}>
                  <div
                    className="bg-brand-black border border-brand-border rounded-xl p-5 md:p-6 transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="flex items-start gap-4">
                      <span className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold font-bold text-[0.9375rem] md:text-[1rem] shrink-0">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.1875rem] leading-[1.7] pt-1">
                        {point}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Core Services Banner */}
        <section className="relative h-[260px] md:h-[340px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20with%20golden%20family%20life%20tree%20and%20flowing%20light%20pathways%20on%20deep%20black%20canvas%2C%20subtle%20generational%20bridge%20motifs%20connecting%20golden%20nodes%2C%20warm%20golden%20glow%20representing%20life%20journey%20and%20legacy%20planning%2C%20premium%20family%20wealth%20planning%20brand%20aesthetic%2C%20sophisticated%20minimalist%20composition%2C%20cinematic%20lighting%2C%204K%20quality&width=1920&height=400&seq=203&orientation=landscape"
            alt="人生核心服務意象"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-black/70" />
        </section>

        {/* Core Services */}
        <section className="py-16 md:py-20">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  三大核心服務
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {coreServices.map((service, index) => (
                <ScrollReveal key={service.number} delay={index * 120}>
                  <div
                    className="bg-brand-card border border-brand-border rounded-2xl p-6 md:p-8 transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-gold/10">
                        <span className="w-6 h-6 flex items-center justify-center text-brand-gold text-xl">
                          <i className={service.icon} />
                        </span>
                      </div>
                      <span className="text-brand-gold font-bold text-[1rem] md:text-[1.125rem]">
                        {service.number}
                      </span>
                    </div>
                    <h3 className="text-white font-semibold text-[1.25rem] md:text-[1.625rem] leading-[1.45] mb-2">
                      {service.title}
                    </h3>
                    <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.1875rem] leading-[1.75] mb-5">
                      {service.desc}
                    </p>
                    <ul className="space-y-2.5">
                      {service.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-brand-text-secondary text-[1rem] md:text-[1.0625rem]">
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* KPI Cards */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  可量化成果
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {kpiCards.map((kpi, index) => (
                <ScrollReveal key={kpi.label} delay={index * 120}>
                  <div
                    className="bg-brand-black border border-brand-border rounded-2xl p-6 md:p-8 text-center transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl bg-brand-gold/10 mx-auto mb-5">
                      <span className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-brand-gold text-xl md:text-2xl">
                        <i className={kpi.icon} />
                      </span>
                    </div>
                    <p className="text-brand-gold font-bold text-[1.5rem] md:text-[2rem] font-display mb-3">
                      {kpi.metric}
                    </p>
                    <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.1875rem] leading-[1.7]">
                      {kpi.label}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 md:py-20">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  合作流程
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {steps.map((step, index) => (
                <ScrollReveal key={step.number} delay={index * 100}>
                  <div
                    className="bg-brand-card border border-brand-border rounded-xl p-5 md:p-6 transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-gold/20 border border-brand-gold flex items-center justify-center shrink-0">
                        <span className="text-brand-gold font-bold text-[0.9375rem] md:text-[1.0625rem]">
                          {step.number}
                        </span>
                      </div>
                      <h3 className="text-white font-semibold text-[1.125rem] md:text-[1.25rem]">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-brand-text-secondary text-[1.0625rem] leading-[1.75]">
                      {step.desc}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-16 md:py-20">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  真實案例與成果
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  client: "新生兒家庭（0 歲寶寶）",
                  pain: "想幫孩子準備教育金，但不知道要準備多少、從哪裡開始。",
                  solution: "以 18 歲出國留學為目標，設計保單 + 基金雙軌教育金路線圖。",
                  result: "每月只需投入 1.2 萬，18 歲時累積約 280 萬教育金，目標達成率 95%。",
                  tag: "教育金規劃",
                },
                {
                  client: "60 歲退休夫妻",
                  pain: "退休後每月固定支出 7 萬，但現金流僅 4 萬，擔心坐吃山空。",
                  solution: "重新配置資產，建立「必要 / 彈性 / 願望」三層現金流，搭配保單給付。",
                  result: "退休現金流提升至每月 7.5 萬，並保留 200 萬醫療與長照緩衝金。",
                  tag: "退休現金流",
                },
                {
                  client: "企業主家族（二代接班）",
                  pain: "家族資產分散，擔心傳承時產生爭議與高額稅負。",
                  solution: "設計保險金信託 + 贈與節點 + 家族共識會議機制。",
                  result: "傳承架構清晰，預估節省遺產稅約 35%，家族成員共識達成率 100%。",
                  tag: "資產傳承",
                },
              ].map((c, index) => (
                <ScrollReveal key={c.tag} delay={index * 120}>
                  <div className="bg-brand-card border border-brand-border rounded-2xl p-6 md:p-8 transition-all duration-300 hover:border-brand-gold/50 h-full flex flex-col">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="inline-block border border-brand-gold/40 text-brand-gold text-[0.875rem] font-medium px-3 py-1 rounded-full">
                        {c.tag}
                      </span>
                    </div>
                    <p className="text-white font-semibold text-[1.125rem] md:text-[1.25rem] mb-4">
                      {c.client}
                    </p>

                    <div className="space-y-4 mb-6 flex-1">
                      <div>
                        <p className="text-brand-gold/80 text-[0.9375rem] font-medium mb-1">面臨問題</p>
                        <p className="text-brand-text-secondary text-[1rem] md:text-[1.0625rem] leading-[1.7]">
                          {c.pain}
                        </p>
                      </div>
                      <div>
                        <p className="text-brand-gold/80 text-[0.9375rem] font-medium mb-1">解決方案</p>
                        <p className="text-brand-text-secondary text-[1rem] md:text-[1.0625rem] leading-[1.7]">
                          {c.solution}
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-brand-border pt-5 mt-auto">
                      <p className="text-brand-gold font-bold text-[1.0625rem] md:text-[1.125rem] leading-[1.7]">
                        {c.result}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Quick Q&A */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  常見問題
                </h2>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="max-w-[800px] space-y-4">
                {[
                  {
                    q: "教育金和退休金可以一起規劃嗎？",
                    a: "可以，而且建議一起看。因為家庭現金流有限，整合規劃能確保兩個目標彼此協調，不會顧此失彼。",
                  },
                  {
                    q: "什麼時候開始準備退休金最好？",
                    a: "越早越好。退休規劃的核心是時間與複利，提早開始，每月需要準備的金額就越低，壓力也越小。",
                  },
                  {
                    q: "家庭成員增加後，規劃需要調整嗎？",
                    a: "是的。結婚、生子、購屋等重大變化都會影響現金流與保障需求，我們會協助你及時更新規劃。",
                  },
                  {
                    q: "保險金信託適合普通家庭嗎？",
                    a: "只要希望資金流向清楚、照護安排有依據，就適合。信託不是有錢人的專利，而是讓愛延續的方法。",
                  },
                ].map((faq, i) => (
                  <details
                    key={i}
                    className="bg-brand-black border border-brand-border rounded-xl group transition-all duration-300 hover:border-brand-gold/40"
                  >
                    <summary className="flex items-start justify-between gap-4 p-5 md:p-6 text-left cursor-pointer list-none">
                      <div className="flex items-start gap-3 md:gap-4">
                        <span className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold font-bold text-[0.875rem] md:text-[1rem] shrink-0 mt-0.5">
                          Q{i + 1}
                        </span>
                        <span className="text-white text-[1.0625rem] md:text-[1.1875rem] font-medium leading-[1.55] pt-1">
                          {faq.q}
                        </span>
                      </div>
                      <span className="w-6 h-6 flex items-center justify-center text-brand-gold shrink-0 mt-1 group-open:rotate-180 transition-transform duration-300">
                        <i className="ri-arrow-down-s-line text-xl" />
                      </span>
                    </summary>
                    <div className="px-5 md:px-6 pb-5 md:pb-6 pl-16 md:pl-[4.25rem]">
                      <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.125rem] leading-[1.75]">
                        {faq.a}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20with%20golden%20family%20silhouettes%20and%20generational%20tree%20branches%20on%20deep%20black%20canvas%2C%20subtle%20warm%20golden%20light%20representing%20life%20journey%20milestones%20and%20legacy%20continuation%2C%20premium%20life%20planning%20brand%20aesthetic%2C%20serene%20and%20hopeful%20atmosphere%20with%20elegant%20gold%20accents%2C%20cinematic%20lighting%2C%204K%20quality&width=1920&height=500&seq=211&orientation=landscape"
              alt="人生門神 CTA 背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/80" />
          </div>
          <div className="container-main relative z-10">
            <ScrollReveal className="text-center max-w-[700px] mx-auto">
              <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4] mb-3">
                人生越重要，越需要提前規劃。
              </h2>
              <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] mb-8">
                預約 30–45 分鐘家庭健診，先釐清，再行動。
              </p>
              <a
                href="https://lin.ee/o51JBHX"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-10 py-4 rounded-full text-[1.125rem] font-semibold hover:bg-brand-gold-soft transition-colors whitespace-nowrap"
              >
                <i className="ri-line-fill" />
                加入 LINE 預約家庭健診
              </a>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}